import React from "react";
import { t } from "ttag";

import { getDocument, getWindow } from "@thelabnyc/thelabui/src/utils/dom";

import { Form } from "../../../forms/Form";
import { FormSubmit } from "../../../forms/FormSubmit";
import {
    IFormUUID,
    IReviewsProductID,
    IReviewsProductVariantID,
    isoFormUUID,
    isoProductUUID,
    isoReviewsProductID,
} from "../../../models/nominals";
import {
    IReviewsProduct,
    IUUIDReviewsInfoMap,
    IWriteReviewTemplate,
    UUIDReviewsInfoMap,
} from "../../../models/reviews";
import { check } from "../../../models/utils";
import { parseFacetValues } from "../../../utils/reviews";
import { useAppDispatch, useAppSelector } from "../../reducers";
import { SideNames, WriteReviewFormMode } from "../constants";
import { defaultReviewTemplate, defaultState } from "../defaults";
import { MultiProductWriteReviewFormGlobalInputSection } from "../elements/MultiProductWriteReviewFormGlobalInputSection";
import { MultiProductWriteReviewFormSection } from "../elements/MultiProductWriteReviewFormSection";
import { WriteReviewFormDescriptionCopy } from "../elements/WriteReviewFormDescriptionCopy";
import { WriteReviewFormDisclaimers } from "../elements/WriteReviewFormDisclaimers";
import {
    IReviewFormProductTemplatePair,
    IWriteReviewFormState,
} from "../models";
import {
    registerNewWriteReviewForm,
    reviewFormVariantChange,
    reviewSetCustomerInformationEmail,
    setWriteReviewFormMode,
} from "../reducers";
import {
    loadProductsByUUIDs,
    loadReviewTemplate,
    submitReview,
} from "../thunks";

interface IParams {
    [name: string]: string | number | number[] | undefined | null;
}

const getWriteReviewTemplateForForm = (
    templates: readonly IReviewFormProductTemplatePair[],
    formState: IWriteReviewFormState,
): IWriteReviewTemplate => {
    const formTemplate = templates.find(
        ({ productID }) => productID === formState.selectedProductID,
    );
    return formTemplate ? formTemplate.template : defaultReviewTemplate;
};

const parseReviewsInfoMap = (infoMapJSON: string): IUUIDReviewsInfoMap => {
    const data = check(UUIDReviewsInfoMap.decode(JSON.parse(infoMapJSON)));
    // TODO: is there a way to do this with io-ts decoder?
    // convert JSON object into Map, converting UUIDstring to UUID
    const infoMap: IUUIDReviewsInfoMap = Object.entries(data)
        .map(([k, v]) => [isoProductUUID.wrap(k), v])
        .reduce((m, [k, v]) => m.set(k, v), new Map());
    return infoMap;
};

const readSelectedProductParam = (): IReviewsProductID[] => {
    const window = getWindow();
    if (!window) {
        return [];
    }
    const urlObj = new URL(window.location.href);
    const query: IParams = {};
    urlObj.searchParams.forEach((value, name) => {
        query[name] = `${value}`;
    });
    if (query.selected_products) {
        const prodIDs: IReviewsProductID[] = parseFacetValues(
            `${query.selected_products || ""}`,
        ).map((id: string) => isoReviewsProductID.wrap(parseInt(id, 10)));
        return prodIDs;
    }
    return [];
};

const scrollToThankYou = () => {
    const document = getDocument();
    if (!document) {
        return;
    }
    const thankYouSelectDiv = document.querySelector(".review-thank-you");
    thankYouSelectDiv?.scrollIntoView({ behavior: "smooth" });
};

const allReviewsSubmitted = (
    infoMap: IUUIDReviewsInfoMap,
    forms: { [formUUID: string]: IWriteReviewFormState },
): boolean => {
    // if no product UUIDs on dom, treat as completed
    if (!infoMap.size) {
        return true;
    }
    const unSubmittedReviews = Object.entries(forms).filter(
        ([_, reviewTemplate]) =>
            reviewTemplate.mode !== WriteReviewFormMode.THANK_YOU_OPEN,
    );
    if (!unSubmittedReviews.length) {
        return true;
    }
    return false;
};

const someReviewsSubmitted = (forms: {
    [formUUID: string]: IWriteReviewFormState;
}): boolean => {
    // if at least one review is submitted and no other review forms open
    const submittedReviews = Object.entries(forms).filter(
        ([_, reviewTemplate]) =>
            reviewTemplate.mode === WriteReviewFormMode.THANK_YOU_OPEN,
    );
    const openReviews = Object.entries(forms).filter(
        ([_, reviewTemplate]) =>
            reviewTemplate.mode === WriteReviewFormMode.FORM_OPEN,
    );
    if (submittedReviews.length && !openReviews.length) {
        return true;
    }
    return false;
};

export const MultiProductWriteReviewForm = (props: {
    reviewsInfoMapJSON: string;
    email?: string;
}): React.ReactNode => {
    const dispatch = useAppDispatch();

    const loadedInitialProducts = useAppSelector((rootState) => {
        const state = rootState.reviews || defaultState;
        return state.data.loadedInitialProducts;
    });

    const products = useAppSelector((rootState) => {
        const state = rootState.reviews || defaultState;
        return state.data.products;
    });

    const writeReviewTemplates = useAppSelector((rootState) => {
        const state = rootState.reviews || defaultState;
        return state.data.writeReviewTemplates;
    });

    const forms = useAppSelector((rootState) => {
        const state = rootState.reviews || defaultState;
        return state.ui.writeReviewForms;
    });

    const infoMap = parseReviewsInfoMap(props.reviewsInfoMapJSON);

    const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const isMultiReview = true;
        for (const [uuid, formState] of Object.entries(forms)) {
            const formTemplate = getWriteReviewTemplateForForm(
                writeReviewTemplates,
                formState,
            );
            if (formState.mode === WriteReviewFormMode.FORM_OPEN) {
                void dispatch(
                    submitReview({
                        formUUID: isoFormUUID.wrap(uuid),
                        template: formTemplate,
                        form: formState,
                        isMultiReview,
                    }),
                );
            }
        }
    };

    // componentDidMount
    React.useEffect(() => {
        const registerNewReviewsForm = async (
            formUUID: IFormUUID,
            product: IReviewsProduct,
            variantID: IReviewsProductVariantID | null = null,
        ) => {
            dispatch(
                registerNewWriteReviewForm({
                    formUUID,
                    mode: WriteReviewFormMode.FORM_OPEN,
                }),
            );
            // Load and dispatch the review template
            await dispatch(
                loadReviewTemplate({
                    formUUID: formUUID,
                    productID: product.id,
                }),
            );
            // Find variant info from review product, update selection of form
            const reviewVariantInfo = product.variants.find(
                (v) => v.id === variantID,
            );
            if (reviewVariantInfo) {
                dispatch(
                    reviewFormVariantChange({
                        formUUID: formUUID,
                        selectedVariant: reviewVariantInfo.slug,
                    }),
                );
            }
        };

        const loadTemplate = async (product: IReviewsProduct) => {
            if (!product.uuid) {
                return;
            }
            const formUUID = isoFormUUID.wrap(String(product.uuid));
            const reviewsMap = infoMap.get(product.uuid);
            // If multiple variants, render separate forms for each variant on product
            if (reviewsMap && reviewsMap.reviewsProductVariantIDs.length > 0) {
                for (const variantID of reviewsMap.reviewsProductVariantIDs) {
                    const variantFormUUID = isoFormUUID.wrap(
                        `${product.uuid}__${variantID}`,
                    );
                    await registerNewReviewsForm(
                        variantFormUUID,
                        product,
                        variantID,
                    );
                }
            } else {
                // No variant info provided
                await registerNewReviewsForm(formUUID, product, null);
            }
        };

        const setActiveProducts = () => {
            const prodIDs = readSelectedProductParam();
            if (!prodIDs.length) {
                return;
            }
            Object.entries(forms).forEach(([uuidstr, formState]) => {
                // make sure selected products are active
                const uuid = isoFormUUID.wrap(uuidstr);
                if (
                    formState.selectedProductID &&
                    prodIDs.includes(formState.selectedProductID)
                ) {
                    dispatch(
                        setWriteReviewFormMode({
                            formUUID: uuid,
                            mode: WriteReviewFormMode.FORM_OPEN,
                        }),
                    );
                    // deactive products not in selected_products param
                } else {
                    dispatch(
                        setWriteReviewFormMode({
                            formUUID: uuid,
                            mode: WriteReviewFormMode.CLOSED,
                        }),
                    );
                }
            });
        };

        const listProducts = async () => {
            // Load the reviews product for each given UUID
            const prodUUIDs = [...infoMap.keys()];
            const result = await dispatch(loadProductsByUUIDs(prodUUIDs));
            if (!loadProductsByUUIDs.fulfilled.match(result)) {
                return;
            }
            const products = result.payload;
            // Load the reviews template for product
            await Promise.all(products.map(loadTemplate));
            if (props.email) {
                dispatch(reviewSetCustomerInformationEmail(props.email));
            }
            setActiveProducts();
        };
        void listProducts();
    }, []);

    // componentDidUpdate - auto-scroll to thank you when reviews are submitted
    React.useEffect(() => {
        if (someReviewsSubmitted(forms)) {
            scrollToThankYou();
        }
    }, [forms]);

    if (!loadedInitialProducts) {
        return null;
    }

    // if all forms submitted or closed, show thank you
    if (allReviewsSubmitted(infoMap, forms)) {
        return (
            <div className="review-thank-you" role="alert">
                <h2 className="review-thank-you__title">
                    {t`No Products Left to Review`}
                </h2>
                <p>
                    {t`We appreciate your feedback and comments, and always enjoy hearing from you.`}
                </p>
                <p>
                    {t`Return to the `}
                    <a
                        title={t`Home`}
                        href="/"
                        className="review-thank-you__home"
                        target="_self"
                    >
                        {t`homepage`}
                    </a>
                    <span>.</span>
                </p>
            </div>
        );
    }

    return (
        <>
            {someReviewsSubmitted(forms) && (
                <div className="review-thank-you" role="alert">
                    <h2 className="review-thank-you__title">
                        {t`Thank You For Your Feedback`}
                    </h2>
                    <p>
                        {t`We appreciate your comments. Please review more items below or return to the `}
                        <a
                            title={t`Home`}
                            href="/"
                            className="review-thank-you__home"
                            target="_self"
                        >
                            {t`homepage`}
                        </a>
                        <span>.</span>
                    </p>
                </div>
            )}
            <div className="multi-product-write-review-form">
                <p className="write-review-form__sub-title">
                    (
                    <span className="write-review-form__sub-title--red">*</span>
                    ){" "}
                    <span className="write-review-form__sub-title--italic">
                        {t`indicates a required field`}
                    </span>
                </p>
                <WriteReviewFormDescriptionCopy isMultiReview={true} />

                <Form onSubmit={onSubmit} noValidate={true}>
                    {products.map((product) => {
                        if (!product.uuid) {
                            return null;
                        }
                        const reviewsMap = infoMap.get(product.uuid);
                        // Is Product Heterogeneous?
                        if (
                            reviewsMap &&
                            reviewsMap.reviewsProductVariantIDs.length > 1
                        ) {
                            return reviewsMap.reviewsProductVariantIDs.map(
                                (
                                    variantID: IReviewsProductVariantID,
                                    idx: number,
                                ) => {
                                    const formUUID = isoFormUUID.wrap(
                                        `${product.uuid}__${variantID}`,
                                    );
                                    const side = SideNames[idx];
                                    return (
                                        <MultiProductWriteReviewFormSection
                                            key={String(formUUID)}
                                            formUUID={formUUID}
                                            product={product}
                                            isMultiReview={true}
                                            side={side}
                                        />
                                    );
                                },
                            );
                            // Products with one variant not considered htereogenous, have no side info
                        } else if (
                            reviewsMap &&
                            reviewsMap.reviewsProductVariantIDs.length === 1
                        ) {
                            return reviewsMap.reviewsProductVariantIDs.map(
                                (variantID: IReviewsProductVariantID) => {
                                    const formUUID = isoFormUUID.wrap(
                                        `${product.uuid}__${variantID}`,
                                    );
                                    return (
                                        <MultiProductWriteReviewFormSection
                                            key={String(formUUID)}
                                            formUUID={formUUID}
                                            product={product}
                                            isMultiReview={true}
                                        />
                                    );
                                },
                            );
                        } else {
                            const formUUID = isoFormUUID.wrap(
                                String(product.uuid),
                            );
                            return (
                                <MultiProductWriteReviewFormSection
                                    key={String(product.uuid)}
                                    formUUID={formUUID}
                                    product={product}
                                    isMultiReview={true}
                                />
                            );
                        }
                    })}
                    <MultiProductWriteReviewFormGlobalInputSection
                        email={props.email}
                    />
                    <WriteReviewFormDisclaimers />
                    <FormSubmit
                        className="button button--rock-blue write-review-form__cta"
                        type="submit"
                        value={t`Submit your review`}
                    />
                </Form>
            </div>
        </>
    );
};
